/** Fra sangdokument til linjer for visning. Rene funksjoner. Merker vises alltid som de er. */
import type { ParsedLine } from '../leadsheetParser';
import { skrivAkkord, tilBokstaver, transponerAkkord, type Akkord } from './akkord';
import { transponerToneart, visToneart, type Toneart } from './toneart';
import type { PdfHode } from './pdf';
import type { SangDokument, SangLinje } from './tolkSang';

export interface VisValg {
  toneart: Toneart | null;
  semitoner: number;
  visning: 'original' | 'bokstaver';
}

type Omskriving = (akkord: Akkord) => Akkord;

/** Sann når sangens bokstavakkorder har minst én rot med b og ingen med #. */
function brukerFlatteRoter(dokument: SangDokument): boolean {
  const fortegn = dokument.linjer.flatMap((linje) =>
    linje.type === 'tekst'
      ? linje.akkorder.flatMap(({ akkord }) => (akkord?.type === 'akkord' && akkord.rot.type === 'bokstav' ? [akkord.rot.fortegn] : []))
      : []
  );
  return fortegn.includes('b') && !fortegn.includes('#');
}

function velgOmskriving(dokument: SangDokument, valg: VisValg): Omskriving {
  const toneart = valg.toneart && transponerToneart(valg.toneart, valg.semitoner);
  const h = dokument.brukerH;
  if (dokument.notasjon === 'tall') {
    if (valg.visning !== 'bokstaver' || !toneart) return (akkord) => akkord;
    // Tall går til måltonearten. Enkelte bokstavakkorder i en tall-fil flyttes med samme stavemåte.
    return (akkord) => tilBokstaver(transponerAkkord(akkord, valg.semitoner, { flatt: toneart.flatt, h }), toneart, { h });
  }
  const flatt = toneart ? toneart.flatt : brukerFlatteRoter(dokument);
  return (akkord) => transponerAkkord(akkord, valg.semitoner, { flatt, h });
}

function tilVisning(linje: SangLinje, omskriv: Omskriving): ParsedLine {
  if (linje.type === 'tom') return { text: '', chords: [], isSection: false };
  if (linje.type === 'seksjon') return { text: linje.tekst, chords: [], isSection: true };
  const chords = linje.akkorder.map(({ tekst, akkord, kolonne }) => ({
    chord: akkord ? skrivAkkord(omskriv(akkord)) : tekst,
    position: kolonne,
  }));
  return { text: linje.tekst, chords, isSection: false };
}

export function visSang(dokument: SangDokument, valg: VisValg): ParsedLine[] {
  const omskriv = velgOmskriving(dokument, valg);
  return dokument.linjer.map((linje) => tilVisning(linje, omskriv));
}

function toneartTekst(toneart: Toneart | null, semitoner: number): string | undefined {
  if (!toneart) return undefined;
  if (semitoner === 0) return visToneart(toneart);
  return `${visToneart(transponerToneart(toneart, semitoner))} (${semitoner > 0 ? '+' : ''}${semitoner})`;
}

/** Hodet for PDF og forhåndsvisning. Tall er like i alle tonearter, så tall-visning viser originaltonearten. */
export function lagHode(dokument: SangDokument, sang: { tittel: string; artist?: string }, valg: VisValg): PdfHode {
  const visesSomTall = dokument.notasjon === 'tall' && valg.visning === 'original';
  return {
    tittel: sang.tittel,
    artist: sang.artist || 'Ukjent artist',
    toneart: toneartTekst(valg.toneart, visesSomTall ? 0 : valg.semitoner),
  };
}

const harBareAkkorder = (linje: SangLinje): boolean => linje.type === 'tekst' && linje.akkorder.length > 0 && !linje.tekst.trim();

/** Bare teksten: akkorder og merker fjernes, og linjer som ikke har annet, tas ut. */
export function kunTekst(dokument: SangDokument): ParsedLine[] {
  return dokument.linjer
    .filter((linje) => !harBareAkkorder(linje))
    .map((linje) => ({ ...tilVisning(linje, (akkord) => akkord), chords: [] }));
}
